import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import constants from 'config/constants'
import { StyledLink } from './index'
import { NavCollapseButton } from 'components/NavCollapseButton'
import { getPageList } from './FeedbackSubMenu'
import { AllowPermission } from 'shared/components/AllowPermission'
import { getFullName } from './utils'
import { useNavbarMenuContext } from './context'
import * as R from 'ramda'
import useAxios from 'hooks/useAxios'
import useWorkspace from 'hooks/useWorkspace'
import store from 'store'
import auth from 'auth'
import usePermission from 'hooks/usePermission'

export default function MobileMainMenu({
  toggleNavBar,
  isNavBarActive,
  onClickLogout,
  user,
  homePageUrl,
}) {
  const context = useNavbarMenuContext()

  const [reportViewPages, setReportViewPages] = useState([])
  const [reportFavorites, setReportFavorites] = useState([])

  const { data: reportFavoriteData } = useAxios({
    url: 'dashboard/list-favorite-view/?type=report',
  })

  const { data: reportViewPage } = useAxios({
    url: 'reports/report-view-pages/',
  })

  const workspaceToken = useWorkspace()

  useEffect(() => {
    if (reportFavoriteData) {
      setReportFavorites(reportFavoriteData.results)
    }

    if (reportViewPage) {
      setReportViewPages(reportViewPage.results)
    }
  }, [reportFavoriteData, reportViewPage])

  return (
    <StyledMobileContainer>
      <Nav className="ui stackable secondary menu main-nav  !bg-white m0" active={isNavBarActive}>
        <HeaderMobileMenu
          toggleNavBar={toggleNavBar}
          isNavBarActive={isNavBarActive}
          homePageUrl={homePageUrl}
        />

        <AllowPermission permission={constants.permissions.viewDashboards}>
          <div className="ui simple dropdown item">
            Dashboards <i className="fas fa-caret-down icon" />
            <div className="menu">
              <FavoriteSurveyDashboardMenu
                context={context}
                toggleNavBar={toggleNavBar}
                workspaceToken={workspaceToken}
              />

              <CustomDashboardMenu
                context={context}
                toggleNavBar={toggleNavBar}
                workspaceToken={workspaceToken}
              />

              <AllowPermission permission={constants.permissions.viewSurveyDashboards}>
                <SurveyWithViewMenu
                  context={context}
                  toggleNavBar={toggleNavBar}
                  workspaceToken={workspaceToken}
                />
              </AllowPermission>
            </div>
          </div>
        </AllowPermission>

        <AllowPermission permission={constants.permissions.viewFeedback}>
          <div className="ui simple dropdown item">
            Feedback <i className="fas fa-caret-down icon" />
            <div className="menu">
              <FavoriteFeedbackMenu
                context={context}
                toggleNavBar={toggleNavBar}
                workspaceToken={workspaceToken}
              />
              <FeedbackWithViewMenu
                context={context}
                toggleNavBar={toggleNavBar}
                workspaceToken={workspaceToken}
              />
            </div>
          </div>
        </AllowPermission>

        <AllowPermission permission={constants.permissions.viewCustomers}>
          <Link className="item" to="/customers/" onClick={toggleNavBar}>
            Customers
          </Link>
        </AllowPermission>

        <AllowPermission permission={constants.permissions.viewTeamMembers}>
          <Link className="item" to={`/${workspaceToken}/team-members/`} onClick={toggleNavBar}>
            Team members
          </Link>
        </AllowPermission>

        <AllowPermission permission={constants.permissions.viewReports}>
          <div className="ui simple dropdown item">
            Reports <i className="fas fa-caret-down icon" />
            <div className="menu">
              <FavoriteReportMenu
                reportFavorites={reportFavorites}
                toggleNavBar={toggleNavBar}
                workspaceToken={workspaceToken}
              />
              <ReportWithViewMenu
                reportViewPages={reportViewPages}
                toggleNavBar={toggleNavBar}
                workspaceToken={workspaceToken}
              />
            </div>
          </div>
        </AllowPermission>

        <AllowPermission permission={constants.permissions.viewIntegrations}>
          <Link
            className="item"
            to={`/${workspaceToken}/settings/integrations/`}
            onClick={toggleNavBar}
          >
            Integrations
          </Link>
        </AllowPermission>

        <AllowPermission permission={constants.permissions.viewSuccessDashboard}>
          <Link
            className="item"
            to={`/${workspaceToken}/success-dashboard/`}
            onClick={toggleNavBar}
          >
            Success dashboard
          </Link>
        </AllowPermission>

        <MobileAdminMenu toggleNavBar={toggleNavBar} workspaceToken={workspaceToken} />

        <MobileProfileMenu
          onClickLogout={onClickLogout}
          user={user}
          toggleNavBar={toggleNavBar}
          workspaceToken={workspaceToken}
        />
      </Nav>
    </StyledMobileContainer>
  )
}

function HeaderMobileMenu({ toggleNavBar, isNavBarActive, homePageUrl }) {
  const HeaderBar = styled.div`
    box-shadow: 0px 2px 2px rgba(88, 102, 126, 0.05);

    padding-left: 20px;
    padding-right: 36px;
  `

  return (
    <HeaderBar>
      <div className="flex items-center justify-between h-100">
        <StyledLink to={homePageUrl}>
          <img width={24} src={constants.simplesatLogoGreenUrl} alt="Simplesat logo" />
        </StyledLink>
        <StyledNavCollapseButton onClick={toggleNavBar} active={isNavBarActive} />
      </div>
    </HeaderBar>
  )
}

const CustomDashboardMenu = ({ context, toggleNavBar, workspaceToken }) => {
  return (
    <React.Fragment>
      <div className="header">Custom dashboards</div>
      {R.pathOr([], ['contextData', 'customDashboards', 'results'], context).map((dashboard) => {
        return (
          <Link
            className="header item"
            key={dashboard.id}
            to={{
              pathname: `/${workspaceToken}/dashboards/${dashboard.token}/`,
              state: { useStickyFilter: false, dashboardName: dashboard.name },
            }}
            onClick={toggleNavBar}
          >
            {dashboard.name}
          </Link>
        )
      })}
      <div className="divider" />
    </React.Fragment>
  )
}

const SurveyWithViewMenu = ({ context, toggleNavBar, workspaceToken }) => {
  return (
    <React.Fragment>
      <div className="header">Surveys</div>

      {R.pathOr([], ['contextData', 'surveyDashboardViews', 'surveys'], context).map(
        (mainSurvey) => {
          return (
            <React.Fragment key={mainSurvey.id}>
              <Link
                className="header item"
                to={{
                  pathname: `/${workspaceToken}/dashboards/views/${mainSurvey.token}/overview/`,
                  state: { useStickyFilter: false, surveyToken: mainSurvey.survey.token },
                }}
                onClick={toggleNavBar}
              >
                {mainSurvey.survey.name}
              </Link>
              {mainSurvey.views.map((view) => {
                return (
                  <Link
                    key={view.id}
                    className="header item !flex"
                    to={{
                      pathname: `/${workspaceToken}/dashboards/views/${view.token}/overview/`,
                      state: { useStickyFilter: false, surveyToken: mainSurvey.survey.token },
                    }}
                    onClick={toggleNavBar}
                  >
                    <i className="text-grey mx-2.5 text-[6px] fas fa-circle" />
                    {view.name}
                  </Link>
                )
              })}
            </React.Fragment>
          )
        }
      )}
      <div className="divider" />
    </React.Fragment>
  )
}

const FeedbackWithViewMenu = ({ context, toggleNavBar, workspaceToken }) => {
  const feedbackViewPages = getPageList('feedbackViewPages', context)

  return (
    <React.Fragment>
      <div className="header">Feedback views</div>
      <Link className="header item" to="/feedback/" onClick={toggleNavBar}>
        All feedback
      </Link>
      {feedbackViewPages.map((feedbackView, index) => {
        return (
          <Link
            key={index}
            className="sub-menu-item item"
            to={{
              pathname: `/${workspaceToken}/feedback/views/${feedbackView.token}/`,
              state: { feedbackViewName: feedbackView.name },
            }}
            onClick={toggleNavBar}
          >
            {feedbackView.name}
          </Link>
        )
      })}
    </React.Fragment>
  )
}

const FavoriteFeedbackMenu = ({ context, toggleNavBar, workspaceToken }) => {
  const favoriteFeedbackPages = getPageList('favoriteFeedbackPages', context).filter(
    (page) => page.type === 'feedback'
  )

  if (favoriteFeedbackPages.length < 1) {
    return null
  }

  return (
    <React.Fragment>
      <div className="header">Favorites</div>
      {favoriteFeedbackPages.map((feedbackView) => {
        return (
          <Link
            key={feedbackView.token}
            className="header item"
            to={{
              pathname: `/${workspaceToken}/feedback/views/${feedbackView.token}/`,
              state: { useStickyFilter: false, feedbackViewName: feedbackView.name },
            }}
            onClick={toggleNavBar}
          >
            {feedbackView.name}
          </Link>
        )
      })}
      <div className="divider" />
    </React.Fragment>
  )
}

const FavoriteSurveyDashboardMenu = ({ context, toggleNavBar, workspaceToken }) => {
  const favoriteDashboardSurveyPages = getPageList('favoriteDashboardSurveyPages', context)

  if (favoriteDashboardSurveyPages.length < 1) {
    return null
  }

  return (
    <React.Fragment>
      <div className="header">Favorites</div>
      {favoriteDashboardSurveyPages.map((view) => {
        if (view.type == 'dashboard') {
          return (
            <Link
              key={view.token}
              className="header item"
              to={{
                pathname: `/${workspaceToken}/dashboards/${view.token}/`,
                state: { useStickyFilter: false, dashboardName: view.name },
              }}
              onClick={toggleNavBar}
            >
              {view.name}
            </Link>
          )
        } else if (view.type == 'survey') {
          return (
            <Link
              key={view.token}
              className="header item"
              onClick={toggleNavBar}
              to={{
                pathname: `/${workspaceToken}/dashboards/views/${view.token}/overview/`,
                state: { useStickyFilter: false, viewName: view.name },
              }}
            >
              {view.is_default_view ? view.survey_name : `${view.name} (${view.survey_name})`}
            </Link>
          )
        }
      })}

      <div className="divider" />
    </React.Fragment>
  )
}

const ReportWithViewMenu = ({ reportViewPages, toggleNavBar, workspaceToken }) => {
  return (
    <React.Fragment>
      <div className="header">Report views</div>
      <Link className="header item" to="/reports/" onClick={toggleNavBar}>
        Report workspace
      </Link>
      {reportViewPages.map((view, index) => {
        return (
          <Link
            key={index}
            className="header item"
            to={{
              pathname: `/${workspaceToken}/reports/views/${view.token}/`,
              state: { reportViewName: view.name },
            }}
            onClick={toggleNavBar}
          >
            {view.name}
          </Link>
        )
      })}
    </React.Fragment>
  )
}

const FavoriteReportMenu = ({ reportFavorites, toggleNavBar, workspaceToken }) => {
  if (reportFavorites.length < 1) {
    return null
  }

  return (
    <React.Fragment>
      <div className="header">Favorites</div>
      {reportFavorites.map((view) => {
        return (
          <Link
            key={view.token}
            className="header item"
            to={{
              pathname: `/${workspaceToken}/reports/views/${view.token}/`,
              state: { reportViewName: view.name },
            }}
            onClick={toggleNavBar}
          >
            {view.name}
          </Link>
        )
      })}
      <div className="divider" />
    </React.Fragment>
  )
}

const MobileProfileMenu = ({ onClickLogout, user, toggleNavBar, workspaceToken }) => {
  const workspaceName = R.path(['selected_workspace', 'name'], user)

  return (
    <div className="ui simple dropdown item">
      {user && getFullName(user)}
      <i className="fas fa-caret-down icon" />
      <div className="menu">
        <React.Fragment>
          <div className="header">
            <i className="far fa-building icon grey" />
            {workspaceName}
          </div>
          {!auth.isDemoUser() && (
            <Link
              className="item"
              to={`/workspaces/`}
              onClick={() => {
                store.remove(constants.simplesatWorkspaceTokenName)
              }}
            >
              Change workspace
            </Link>
          )}
          <div className="divider" />
        </React.Fragment>
        <Link className="item" to={`/${workspaceToken}/profile/edit/`} onClick={toggleNavBar}>
          Edit profile
        </Link>
        <Link className="item" to={`/${workspaceToken}/profile/security/`} onClick={toggleNavBar}>
          Security
        </Link>

        {!auth.isDemoUser() && (
          <>
            <AllowPermission permission={constants.permissions.viewProfileNotification}>
              <Link
                className="item"
                to={`/${workspaceToken}/profile/notifications/`}
                onClick={toggleNavBar}
              >
                Notifications
              </Link>
            </AllowPermission>
            <AllowPermission permission={constants.permissions.viewProfileSummary}>
              <Link
                className="item"
                to={`/${workspaceToken}/profile/summaries/`}
                onClick={toggleNavBar}
              >
                Email summaries
              </Link>
            </AllowPermission>
          </>
        )}

        <div className="divider" />
        <a className="item" onClick={() => window.Intercom('show')}>
          <i className="far fa-comment-question icon" />
          Get help
        </a>
        <a
          className="item"
          href="https://www.simplesat.io/release-notes"
          target="_blank"
          rel="noreferrer"
        >
          <i className="far fa-rocket icon" />
          Release notes
        </a>
        <div className="divider" />
        {!auth.isDemoUser() && (
          <a className="item" onClick={onClickLogout}>
            <i className="far fa-power-off icon" />
            Log out
          </a>
        )}
      </div>
    </div>
  )
}

const MobileAdminMenu = ({ toggleNavBar, workspaceToken }) => {
  const { hasPermission } = usePermission()
  const hasManageAuthenticationSecurity = hasPermission(
    constants.permissions.manageAuthenticationSecurity
  )
  const hasAccessKeyPermission = hasPermission(constants.permissions.accessApiKey)

  return (
    <div className="ui simple dropdown item">
      Admin <i className="fas fa-caret-down icon" />
      <div className="menu">
        <AllowPermission permission={constants.permissions.viewSettings}>
          <Link
            className="item"
            to={`/${workspaceToken}/settings/workspace/`}
            onClick={toggleNavBar}
          >
            Workspace settings
          </Link>
          <Link className="item" to={`/${workspaceToken}/settings/brands/`} onClick={toggleNavBar}>
            Brands
          </Link>
          <Link className="item" to={`/${workspaceToken}/settings/surveys/`} onClick={toggleNavBar}>
            Surveys
          </Link>
          <Link className="item" to={`/${workspaceToken}/settings/topics/`}>
            Topics
          </Link>

          <Link className="item" to={`/${workspaceToken}/settings/roles/`}>
            Roles
          </Link>

          <Link className="item" to={`/${workspaceToken}/settings/email/`} onClick={toggleNavBar}>
            Email authentication
          </Link>
          <Link
            className="item"
            to={`/${workspaceToken}/settings/feedback-widgets`}
            onClick={toggleNavBar}
          >
            Feedback widgets
          </Link>
          <Link className="item" to={`/${workspaceToken}/settings/users/`} onClick={toggleNavBar}>
            Users
          </Link>

          {hasManageAuthenticationSecurity && (
            <Link className="item" to={`/${workspaceToken}/settings/authentication/`}>
              Authentication security
            </Link>
          )}

          {hasAccessKeyPermission && (
            <Link className="item" to={`/${workspaceToken}/settings/api-keys/`}>
              API keys
            </Link>
          )}

          <Link className="item" to={`/${workspaceToken}/settings/usage/`}>
            Usage
          </Link>
        </AllowPermission>

        <AllowPermission permission={constants.permissions.viewBillingPage}>
          <Link className="item" to={`/${workspaceToken}/settings/billing/`} onClick={toggleNavBar}>
            Billing
          </Link>
        </AllowPermission>
      </div>
    </div>
  )
}

const StyledMobileContainer = styled.div`
  @media (max-width: 767px) {
    display: block;
  }
  display: none;
`

const Nav = styled.nav`
  &.menu.main-nav {
    @media (max-width: 767px) {
      height: ${({ active }) => active && 'unset !important'};
      overflow: ${({ active }) => (active ? 'visible' : 'hidden')};
    }
    position: fixed;
    height: 50px;
    width: 100%;
    margin-bottom: 0;
    z-index: 1001;
    box-shadow: 0px 2px 2px rgba(88, 102, 126, 0.05);
  }

  &&.ui.secondary.menu .item {
    padding: 14px 32px !important;

    .dropdown .menu .item {
      padding: 14px 32px !important;
    }
  }

  && .ui.simple.dropdown > .menu {
    overflow: scroll;
    max-height: calc(100vh - 200px);
  }

  && i.icon:last-child {
    margin: 0 0.25rem 0 0;
  }
`

export const StyledNavCollapseButton = styled(NavCollapseButton)`
  @media (max-width: 767px) {
    display: initial !important;
    float: right;
  }

  display: none !important;
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/NavBar/MobileMenu.js